import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTenant } from "@/hooks/useTenant";
import { useFirmMetrics } from "@/hooks/useFirmMetrics";
import { FirmKpiCards } from "@/components/firm/FirmKpiCards";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Building2,
  Network,
  Users,
  RefreshCw,
  TrendingUp,
  ArrowRight,
  Loader2,
  Database,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { chartColors } from "@/lib/theme/chartTheme";

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export default function FirmDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { orgId, isMFO, isLoading: tenantLoading } = useTenant();
  const { firmMetrics, topGPs, sharedFunds, isLoading, error, refetch } = useFirmMetrics();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refresh firm metrics by calling the database functions
  const handleRefreshMetrics = async () => {
    if (!orgId) return;

    setIsRefreshing(true);
    try {
      // Call refresh_firm_metrics function
      const { error: metricsError } = await supabase.rpc('refresh_firm_metrics', {
        p_org_id: orgId
      });

      if (metricsError) throw metricsError;

      // Call refresh_cross_family_exposure function
      const { error: exposureError } = await supabase.rpc('refresh_cross_family_exposure', {
        p_org_id: orgId
      });

      if (exposureError) throw exposureError;

      // Refetch the data
      await refetch();

      toast({
        title: "Metrics refreshed",
        description: "Firm metrics have been recalculated from the latest data.",
      });
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      toast({
        title: "Refresh failed",
        description: err instanceof Error ? err.message : "Failed to refresh metrics",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Prepare KPI data - MUST be before any conditional returns (React hooks rules)
  const kpiData = useMemo(() => ({
    totalAum: firmMetrics?.total_aum_base ?? null,
    familyCount: firmMetrics?.family_count ?? 0,
    aumChange: firmMetrics?.aum_change_ytd ?? null,
    documentsPending: firmMetrics?.documents_pending ?? 0,
    staleNavCount: firmMetrics?.stale_nav_count ?? 0,
    openActionsCount: firmMetrics?.open_actions_count ?? 0,
  }), [firmMetrics]);

  // Prepare AUM by family for pie chart
  const aumByFamilyData = useMemo(() => {
    if (!firmMetrics?.aum_by_family) return [];
    return firmMetrics.aum_by_family.map((f, i) => ({
      name: f.family_name || 'Unknown',
      value: f.aum || 0,
      color: chartColors[i % chartColors.length],
    }));
  }, [firmMetrics]);

  // Prepare top GPs for bar chart
  const topGPsData = useMemo(() => {
    return topGPs.slice(0, 5).map(gp => ({
      name: gp.name.length > 15 ? gp.name.substring(0, 15) + '...' : gp.name,
      fullName: gp.name,
      commitment: gp.totalCommitment,
      fundCount: gp.fundCount,
      familyCount: gp.familyCount,
    }));
  }, [topGPs]);

  // Redirect SFO users to regular dashboard (after all hooks)
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-destructive">Failed to load firm metrics</p>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  if (tenantLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Firm Dashboard
          </h1>
          <p className="text-muted-foreground">
            Aggregate view across all families
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <Users className="h-3 w-3" />
            {kpiData.familyCount} Families
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshMetrics}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Database className="h-4 w-4 mr-1" />
            )}
            Recalculate
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <FirmKpiCards metrics={kpiData} />

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* AUM by Family */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">AUM by Family</CardTitle>
              <CardDescription>Distribution across client families</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/firm/families')}>
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {aumByFamilyData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aumByFamilyData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                    <YAxis type="category" dataKey="name" width={120} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label) => `Family: ${label}`}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {aumByFamilyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No family data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top GPs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Top GP Relationships</CardTitle>
              <CardDescription>Largest commitments by GP</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/firm/gp-exposure')}>
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {topGPsData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topGPsData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label, payload) => {
                        const item = payload?.[0]?.payload;
                        return item ? `${item.fullName} (${item.fundCount} funds, ${item.familyCount} families)` : label;
                      }}
                    />
                    <Bar dataKey="commitment" fill={chartColors[0]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No GP data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shared Funds - Deal Sharing Opportunities */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Network className="h-5 w-5" />
                Cross-Family Fund Exposure
              </CardTitle>
              <CardDescription>
                Funds with multiple families invested - leverage for GP negotiations
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/firm/gp-exposure')}>
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {sharedFunds.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fund</TableHead>
                    <TableHead>GP</TableHead>
                    <TableHead className="text-right">Total Commitment</TableHead>
                    <TableHead className="text-right">Total NAV</TableHead>
                    <TableHead className="text-center">Families</TableHead>
                    <TableHead>Family Breakdown</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sharedFunds.slice(0, 5).map((fund) => (
                    <TableRow key={fund.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">{fund.fund_name}</TableCell>
                      <TableCell className="text-muted-foreground">{fund.gp_name || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(fund.total_commitment_base)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(fund.total_nav_base)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{fund.family_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {fund.family_exposures?.slice(0, 3).map((fe, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {fe.family_name}: {formatCurrency(fe.commitment || 0)}
                            </Badge>
                          ))}
                          {fund.family_exposures?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{fund.family_exposures.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No cross-family fund exposure data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>Common firm-level tasks</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2">
            <Button variant="outline" className="justify-start" onClick={() => navigate('/firm/families')}>
              <Users className="h-4 w-4" />
              View All Families
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/firm/gp-exposure')}>
              <Network className="h-4 w-4" />
              Analyze GP Exposure
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/firm/copilot')}>
              <TrendingUp className="h-4 w-4" />
              Ask Firm Copilot
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate('/firm/agents')}>
              <RefreshCw className="h-4 w-4" />
              Manage Firm Agents
            </Button>
          </CardContent>
        </Card>

        {/* Family Performance Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Family Performance</CardTitle>
            <CardDescription>YTD returns by family</CardDescription>
          </CardHeader>
          <CardContent>
            {firmMetrics?.aum_by_family && firmMetrics.aum_by_family.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Family</TableHead>
                    <TableHead className="text-right">AUM</TableHead>
                    <TableHead className="text-right">Return</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {firmMetrics.aum_by_family.slice(0, 5).map((family, i) => (
                    <TableRow
                      key={family.family_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/firm/families/${family.family_id}`)}
                    >
                      <TableCell className="font-medium flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: chartColors[i % chartColors.length] }}
                        />
                        {family.family_name}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(family.aum)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {family.weighted_twr_ytd !== undefined && family.weighted_twr_ytd !== null
                          ? `${family.weighted_twr_ytd >= 0 ? '+' : ''}${(family.weighted_twr_ytd * 100).toFixed(1)}%`
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No family performance data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
